import { createRoot } from "react-dom/client";
import App, { preloadRoute } from "./App.tsx";
import "./index.css";
// Self-hosted Latin faces (no request to Google Fonts before consent). Weights match tailwind's font stack.
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import "@fontsource/plus-jakarta-sans/latin-500.css";
import "@fontsource/plus-jakarta-sans/latin-600.css";
import "@fontsource/plus-jakarta-sans/latin-700.css";
import "@fontsource/plus-jakarta-sans/latin-800.css";
import "@fontsource/jetbrains-mono/latin-400.css";
import "@fontsource/jetbrains-mono/latin-500.css";
// Chinese pages use the system CJK fonts (see index.css). A web CJK font was 25+ slices, 1.4 MB, and delayed the
// first render by seconds on slow links.

// A deploy replaces the hashed /assets files, so a tab (or briefly cached
// HTML, see public/_headers) from the previous build can fail to load a lazy
// route chunk. Reload to pick up the new build, at most once a minute so a
// genuinely missing file cannot loop.
window.addEventListener("vite:preloadError", (event) => {
  const key = "jsc-chunk-reload-at";
  try {
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

// In production the main stylesheet is preloaded without blocking first paint
// (the pre-rendered heading is styled inline). Mount React only once it has
// applied, so the app never renders unstyled. Falls back after 4 s.
function waitForAppCss(): Promise<void> {
  const link = document.querySelector<HTMLLinkElement>("link[data-app-css]");
  if (!link) return Promise.resolve();
  const applied = () =>
    link.rel === "stylesheet" &&
    Array.from(document.styleSheets).some((s) => s.href === link.href);
  if (applied()) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (link.rel !== "stylesheet") link.rel = "stylesheet";
      requestAnimationFrame(() => resolve());
    };
    link.addEventListener("load", finish, { once: true });
    link.addEventListener("error", finish, { once: true });
    const poll = window.setInterval(() => {
      if (applied()) {
        window.clearInterval(poll);
        finish();
      }
    }, 50);
    window.setTimeout(() => {
      window.clearInterval(poll);
      finish();
    }, 4000);
  });
}

// Load the current page's chunk (and data, for blog posts) alongside the CSS,
// so the first render replaces the prerendered HTML with the same page rather
// than the route spinner.
// The body and heading faces swap in after first paint otherwise, and the
// fallback font wraps text differently (layout shift). Wait for the faces the
// page uses, but never longer than 1.5 s.
function waitForFonts(): Promise<void> {
  if (!document.fonts?.load) return Promise.resolve();
  const faces = ["400 1em Inter", "500 1em Inter", "600 1em Inter", "700 1em Inter", "600 1em 'Plus Jakarta Sans'", "700 1em 'Plus Jakarta Sans'"];
  const loaded = Promise.all(faces.map((f) => document.fonts.load(f))).then(() => undefined, () => undefined);
  return Promise.race([loaded, new Promise<void>((resolve) => window.setTimeout(resolve, 1500))]);
}

Promise.all([waitForAppCss().then(waitForFonts), preloadRoute(window.location.pathname)]).then(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});
